import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../context/LanguageContext";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Book as BookIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  Timer as TimerIcon,
  People as PeopleIcon,
  Rule as RuleIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { bookingTemplatesAPI, contactsAPI } from "../services/api";
import { toast } from "react-toastify";

const ACCENT_COLOR = "#FF8C42";
const GRAY_LIGHT = "#f5f5f5";
const GRAY_BORDER = "#e0e0e0";
const GRAY_TEXT = "#616161";
const GRAY_TEXT_DARK = "#424242";

/** Availability limits (add + edit template) */
const MAX_ADVANCE_BOOKING_DAYS_MIN = 1;
const MAX_ADVANCE_BOOKING_DAYS_MAX = 60;
const MIN_ADVANCE_BOOKING_HOURS_MIN = 1;
const MIN_ADVANCE_BOOKING_HOURS_MAX = 24;
const BUFFER_TIME_MINUTES_MIN = 0;
const BUFFER_TIME_MINUTES_MAX = 60;

const clampIntInclusive = (value, min, max, fallback) => {
  const n = Math.trunc(Number(value));
  if (value === "" || value == null || Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const EMPTY_FORM = {
  name: "",
  description: "",
  duration: 60,
  maxCapacity: 1,
  contactId: "",
  approvalType: "AUTO",
  isActive: true,
  maxAdvanceBooking: 30,
  minAdvanceBooking: 24,
  bufferTime: 15,
  allowCancellation: true,
  cancellationDeadline: 168,
  requiresDeposit: false,
  depositAmount: 0,
  autoReminders: true,
  reminderTimes: [24],
  workingHours: {
    monday: { start: "09:00", end: "17:00", enabled: true },
    tuesday: { start: "09:00", end: "17:00", enabled: true },
    wednesday: { start: "09:00", end: "17:00", enabled: true },
    thursday: { start: "09:00", end: "17:00", enabled: true },
    friday: { start: "09:00", end: "17:00", enabled: true },
    saturday: { start: "09:00", end: "17:00", enabled: false },
    sunday: { start: "09:00", end: "17:00", enabled: false },
  },
  blackoutDates: [],
  /** null + null = “no pricing”; fixed uses number (incl. 0); range uses object + price null */
  price: null,
  priceRange: null,
};

const WORKING_DAY_KEYS = Object.keys(EMPTY_FORM.workingHours);

/** Parse "HH:mm" to minutes since midnight for working-hour validation */
const timeToMinutes = (tStr) => {
  if (tStr == null || tStr === "") return NaN;
  const s = String(tStr);
  const parts = s.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

/** Keys used when merging step-specific validation in the create wizard */
const AVAILABILITY_ERROR_KEYS = [
  "maxAdvanceBooking",
  "minAdvanceBooking",
  "bufferTime",
  ...WORKING_DAY_KEYS.map((d) => `workingHour_${d}`),
];
const SETTINGS_ERROR_KEYS = ["cancellationDeadline", "depositAmount", "reminderTimes"];
const PRICING_ERROR_KEYS = ["price", "priceRangeMin", "priceRangeMax"];

/** Merge API workingHours with defaults so UI always has all days */
const mergeWorkingHours = (fromApi) => {
  const base = EMPTY_FORM.workingHours;
  if (!fromApi || typeof fromApi !== "object") return { ...base };
  const out = { ...base };
  Object.keys(base).forEach((day) => {
    const w = fromApi[day];
    if (w && typeof w === "object") {
      out[day] = {
        start: w.start ?? base[day].start,
        end: w.end ?? base[day].end,
        enabled: typeof w.enabled === "boolean" ? w.enabled : base[day].enabled,
      };
    }
  });
  return out;
};

const resolveContactIdFromApi = (data, fallbackContactId) => {
  if (data.contactId != null && data.contactId !== "") return data.contactId;
  return fallbackContactId ?? "";
};

const parseApiPriceRange = (priceRange) => {
  if (!priceRange || typeof priceRange !== "object") return { min: 0, max: 0 };
  return { min: Number(priceRange.min) || 0, max: Number(priceRange.max) || 0 };
};

const resolvePriceFieldsFromApi = (data) => {
  const pr = parseApiPriceRange(data.priceRange);
  if (pr.max > 0 || pr.min > 0) {
    return { priceRange: pr, price: null };
  }

  const priceFromApi = data.price != null && data.price !== "" ? Number(data.price) : null;
  if (priceFromApi != null && !Number.isNaN(priceFromApi)) {
    return { price: priceFromApi, priceRange: null };
  }

  return { price: null, priceRange: null };
};

const DEFAULT_PRICE_RANGE = { min: 0, max: 0 };

const buildPricingPayloadFields = (formData) => {
  if (formData.price != null) {
    return {
      price: Number(formData.price) || 0,
      priceRange: DEFAULT_PRICE_RANGE,
    };
  }
  if (formData.priceRange != null && typeof formData.priceRange === "object") {
    return {
      price: 0,
      priceRange: {
        min: Number(formData.priceRange.min) || 0,
        max: Number(formData.priceRange.max) || 0,
      },
    };
  }
  return { price: 0, priceRange: DEFAULT_PRICE_RANGE };
};

const getFormPricingType = (formData) => {
  if (formData.price != null) return "fixed";
  if (formData.priceRange != null && typeof formData.priceRange === "object") return "range";
  return "none";
};

const applyPricingTypeChange = (prev, pricingType) => {
  if (pricingType === "fixed") {
    return { ...prev, price: 0, priceRange: null };
  }
  if (pricingType === "range") {
    return { ...prev, price: null, priceRange: { min: 0, max: 0 } };
  }
  return { ...prev, price: null, priceRange: null };
};

const getTemplatePriceChipLabel = (template, translate) => {
  if (template.price) {
    return `$${template.price}`;
  }
  if (template.priceRange?.max > 0) {
    return `$${template.priceRange.min} - $${template.priceRange.max}`;
  }
  return translate("common.free");
};

const getApprovalTypeLabel = (approvalType, translate) => {
  const normalized = String(approvalType || "").toUpperCase();
  if (approvalType === "AUTO" || normalized === "AUTO") {
    return translate("pages.bookingTemplates.approval.auto");
  }
  if (approvalType === "MANUAL" || normalized === "MANUAL") {
    return translate("pages.bookingTemplates.approval.manual");
  }
  return translate("pages.bookingTemplates.approval.partial");
};

const getMaxParticipantsLabel = (template, translate) => {
  const count = template.maxCapacity ?? template.maxParticipants ?? 1;
  if (count > 1) {
    return translate("pages.bookingTemplates.card.maxParticipantsPlural", { n: count });
  }
  return translate("pages.bookingTemplates.card.maxParticipants", { n: count });
};

const parseMaxCapacityInput = (raw) =>
  raw === "" ? "" : Math.max(1, Number.parseInt(raw, 10) || 1);

const sanitizeReminderTimesFromApi = (reminderTimes) => {
  if (!Array.isArray(reminderTimes) || !reminderTimes.length) return [24];
  const sanitized = reminderTimes
    .map((n) => Math.max(0, Math.trunc(Number(n))))
    .filter((n) => !Number.isNaN(n));
  return sanitized.length ? [...new Set(sanitized)] : [24];
};

const sanitizeBlackoutDatesFromApi = (blackoutDates) =>
  (Array.isArray(blackoutDates) ? blackoutDates.map(String) : []);

/** Map GET template response → form state */
const mapApiResponseToForm = (data, fallbackContactId) => {
  const { price, priceRange } = resolvePriceFieldsFromApi(data);
  const {
    price: _rawPrice,
    priceRange: _rawPriceRange,
    maxParticipants,
    maxCapacity: rawMaxCapacity,
    ...restData
  } = data;
  return {
    ...EMPTY_FORM,
    ...restData,
    maxCapacity: rawMaxCapacity ?? maxParticipants ?? 1,
    contactId: resolveContactIdFromApi(data, fallbackContactId),
    reminderTimes: sanitizeReminderTimesFromApi(data.reminderTimes),
    workingHours: mergeWorkingHours(data.workingHours),
    blackoutDates: sanitizeBlackoutDatesFromApi(data.blackoutDates),
    priceRange,
    price,
    maxAdvanceBooking: clampIntInclusive(
      data.maxAdvanceBooking,
      MAX_ADVANCE_BOOKING_DAYS_MIN,
      MAX_ADVANCE_BOOKING_DAYS_MAX,
      EMPTY_FORM.maxAdvanceBooking
    ),
    minAdvanceBooking: clampIntInclusive(
      data.minAdvanceBooking,
      MIN_ADVANCE_BOOKING_HOURS_MIN,
      MIN_ADVANCE_BOOKING_HOURS_MAX,
      EMPTY_FORM.minAdvanceBooking
    ),
    bufferTime: clampIntInclusive(
      data.bufferTime,
      BUFFER_TIME_MINUTES_MIN,
      BUFFER_TIME_MINUTES_MAX,
      EMPTY_FORM.bufferTime
    ),
  };
};

const createTemplateValidationAccumulator = () => {
  const errors = {};
  let firstTab = null;
  const add = (tab, key, msg) => {
    if (!msg) return;
    if (!errors[key]) errors[key] = msg;
    firstTab = firstTab === null ? tab : Math.min(firstTab, tab);
  };
  return { errors, add, getResult: () => ({ errors, firstTab: firstTab ?? 0 }) };
};

const validateWorkingHoursFields = (workingHours, add, translate) => {
  const wh = workingHours || EMPTY_FORM.workingHours;
  WORKING_DAY_KEYS.forEach((day) => {
    const row = wh[day];
    if (!row?.enabled) return;
    const startM = timeToMinutes(row.start);
    const endM = timeToMinutes(row.end);
    let msg = "";
    if (Number.isNaN(startM) || Number.isNaN(endM)) {
      msg = translate("pages.bookingTemplates.validation.invalidTime");
    } else if (endM <= startM) {
      msg = translate("pages.bookingTemplates.validation.endAfterStart");
    }
    add(1, `workingHour_${day}`, msg);
  });
};

const validateMinAdvanceAgainstMax = (fd, errors, add, translate) => {
  const maxD = Number(fd.maxAdvanceBooking);
  const minH = Number(fd.minAdvanceBooking);
  if (
    errors.minAdvanceBooking ||
    errors.maxAdvanceBooking ||
    !Number.isInteger(maxD) ||
    !Number.isInteger(minH) ||
    minH <= maxD * 24
  ) {
    return;
  }
  add(1, "minAdvanceBooking", translate("pages.bookingTemplates.validation.minAdvanceVsMax"));
};

const validateReminderTimesFields = (fd, add, validateNonNegativeInt, translate) => {
  const arr = Array.isArray(fd.reminderTimes) ? fd.reminderTimes : [];
  if (arr.length === 0) {
    add(2, "reminderTimes", translate("pages.bookingTemplates.validation.atLeastOneReminder"));
    return;
  }
  for (let i = 0; i < arr.length; i += 1) {
    const msg = validateNonNegativeInt(arr[i]);
    if (msg) {
      add(2, "reminderTimes", msg);
      break;
    }
  }
};

const validateSettingsFields = (fd, add, validators, translate) => {
  if (fd.allowCancellation) {
    add(2, "cancellationDeadline", validators.validateNonNegativeInt(fd.cancellationDeadline));
  }
  if (fd.requiresDeposit) {
    add(2, "depositAmount", validators.validateDepositAmount(fd.depositAmount));
  }
  if (fd.autoReminders) {
    validateReminderTimesFields(fd, add, validators.validateNonNegativeInt, translate);
  }
};

const validatePricingFields = (fd, add, validators, translate) => {
  const maxPrice = 2147483647;
  if (fd.price != null) {
    const error = validators.validateNonNegativeNumber(fd.price) || (Number(fd.price) > maxPrice ? "Price must be 2,147,483,647 or less." : "");
    add(3, "price", error);
    return;
  }
  if (fd.priceRange != null && typeof fd.priceRange === "object") {
    const minP = Number(fd.priceRange.min);
    const maxP = Number(fd.priceRange.max);
    let minErr = "";
    let maxErr = "";
    if (fd.priceRange.min === "" || fd.priceRange.min == null || Number.isNaN(minP)) {
      minErr = translate("pages.bookingTemplates.validation.required");
    } else if (minP < 0) {
      minErr = translate("pages.bookingTemplates.validation.nonNegative");
    } else if (minP > maxPrice) {
      minErr = "Price must be 2,147,483,647 or less.";
    }
    if (fd.priceRange.max === "" || fd.priceRange.max == null || Number.isNaN(maxP)) {
      maxErr = translate("pages.bookingTemplates.validation.required");
    } else if (maxP < 0) {
      maxErr = translate("pages.bookingTemplates.validation.nonNegative");
    } else if (maxP > maxPrice) {
      maxErr = "Price must be 2,147,483,647 or less.";
    }
    add(3, "priceRangeMin", minErr);
    add(3, "priceRangeMax", maxErr);
    if (minErr === "" && maxErr === "" && minP > maxP) {
      add(3, "priceRangeMax", translate("pages.bookingTemplates.validation.priceRangeOrder"));
    }
  }
};

const runTemplateFormValidation = (fd, translate, validators) => {
  const { errors, add, getResult } = createTemplateValidationAccumulator();

  const nameTrimmed = String(fd.name ?? "").trim();
  const nameErr = nameTrimmed === "" ? translate("pages.bookingTemplates.validation.required") : "";
  add(0, "name", nameErr);
  add(0, "maxCapacity", validators.validateMaxCapacity(fd.maxCapacity));
  add(0, "duration", validators.validateDuration(fd.duration));

  add(1, "maxAdvanceBooking", validators.validateMaxAdvanceBookingDays(fd.maxAdvanceBooking));
  add(1, "minAdvanceBooking", validators.validateMinAdvanceBookingHours(fd.minAdvanceBooking));
  add(1, "bufferTime", validators.validateBufferTimeMinutes(fd.bufferTime));

  validateMinAdvanceAgainstMax(fd, errors, add, translate);
  validateWorkingHoursFields(fd.workingHours, add, translate);
  validateSettingsFields(fd, add, validators, translate);
  validatePricingFields(fd, add, validators, translate);

  return getResult();
};

/** Extract message from API error shape: `{ error: { message } }` or top-level `message` */
const getApiErrorMessage = (err, fallback = "") =>
  err?.response?.data?.error?.message ||
  err?.response?.data?.message ||
  err?.response?.data?.errorMessage ||
  fallback;

const isLikelyDurationFieldError = (msg) => {
  if (!msg || typeof msg !== "string") return false;
  return /\bduration\b/i.test(msg);
};

/** Prevent negative values on numeric booking fields (spinner + typed input). */
const clampBookingNumericInput = (name, value) => {
  if (value === "" || value === null || value === undefined) return value;
  if (typeof value === "string" && value.trim() === "-") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  switch (name) {
    case "duration":
      return String(Math.min(1440, Math.max(15, Math.trunc(n))));
    case "maxAdvanceBooking":
      return String(
        Math.min(MAX_ADVANCE_BOOKING_DAYS_MAX, Math.max(MAX_ADVANCE_BOOKING_DAYS_MIN, Math.trunc(n)))
      );
    case "minAdvanceBooking":
      return String(
        Math.min(MIN_ADVANCE_BOOKING_HOURS_MAX, Math.max(MIN_ADVANCE_BOOKING_HOURS_MIN, Math.trunc(n)))
      );
    case "bufferTime":
      return String(
        Math.min(BUFFER_TIME_MINUTES_MAX, Math.max(BUFFER_TIME_MINUTES_MIN, Math.trunc(n)))
      );
    case "cancellationDeadline":
      return String(Math.max(0, Math.trunc(n)));
    case "depositAmount":
    case "price":
      return String(Math.max(0, n));
    case "maxCapacity":
      return String(Math.min(5000, Math.max(1, Math.trunc(n))));
    default:
      return value;
  }
};

export default function BookingTemplates() {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  /** Create flow: 0 General → 1 Availability → 2 Settings → 3 Pricing (submit) */
  const [createStep, setCreateStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => { fetchTemplates(); fetchContacts(); }, [t]);

  const fetchContacts = async () => {
    try {
      const response = await contactsAPI.getContacts('', '', 0, 100);
      const data = response?.data;
      const list = data?.data ?? data?.payload ?? (Array.isArray(data) ? data : []);
      setContacts(Array.isArray(list) ? list : []);
    } catch (err) { console.error("Error fetching contacts:", err); }
  };

  const fetchTemplates = async () => {
    setIsLoading(true); setError(null);
    try {
      const response = await bookingTemplatesAPI.getTemplates();
      const body = response?.data;
      const list = body?.data ?? body?.payload ?? (Array.isArray(body) ? body : []);
      if (Array.isArray(list)) setTemplates(list);
      else setError(t("pages.bookingTemplates.error.loadFailed"));
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(t("pages.bookingTemplates.error.fetch"));
    } finally { setIsLoading(false); }
  };

  const validateMaxCapacity = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < 1) return t("pages.bookingTemplates.validation.minOne");
    if (n > 5000) return t("pages.bookingTemplates.validation.maxParticipantsRange");
    return "";
  }, [t]);

  /** API: duration must be 15–1440 minutes */
  const validateDuration = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < 15 || n > 1440) return t("pages.bookingTemplates.validation.durationRange");
    return "";
  }, [t]);

  const validateNonNegativeInt = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < 0) return t("pages.bookingTemplates.validation.nonNegative");
    return "";
  }, [t]);

  const validateMaxAdvanceBookingDays = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < MAX_ADVANCE_BOOKING_DAYS_MIN || n > MAX_ADVANCE_BOOKING_DAYS_MAX) {
      return t("pages.bookingTemplates.validation.maxAdvanceDaysRange");
    }
    return "";
  }, [t]);

  const validateMinAdvanceBookingHours = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < MIN_ADVANCE_BOOKING_HOURS_MIN || n > MIN_ADVANCE_BOOKING_HOURS_MAX) {
      return t("pages.bookingTemplates.validation.minAdvanceHoursRange");
    }
    return "";
  }, [t]);

  const validateBufferTimeMinutes = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (!Number.isInteger(n)) return t("pages.bookingTemplates.validation.wholeNumber");
    if (n < BUFFER_TIME_MINUTES_MIN || n > BUFFER_TIME_MINUTES_MAX) {
      return t("pages.bookingTemplates.validation.bufferTimeRange");
    }
    return "";
  }, [t]);

  const validateNonNegativeNumber = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (n < 0) return t("pages.bookingTemplates.validation.nonNegative");
    return "";
  }, [t]);

  const validateDepositAmount = useCallback((value) => {
    const n = Number(value);
    if (value === "" || value == null || Number.isNaN(n)) return t("pages.bookingTemplates.validation.required");
    if (n <= 0) return t("pages.bookingTemplates.validation.depositPositive");
    return "";
  }, [t]);

  const validateTemplateForm = useCallback(
    (fd) =>
      runTemplateFormValidation(fd, t, {
        validateMaxCapacity,
        validateDuration,
        validateNonNegativeInt,
        validateMaxAdvanceBookingDays,
        validateMinAdvanceBookingHours,
        validateBufferTimeMinutes,
        validateNonNegativeNumber,
        validateDepositAmount,
      }),
    [
      t,
      validateMaxCapacity,
      validateDuration,
      validateNonNegativeInt,
      validateMaxAdvanceBookingDays,
      validateMinAdvanceBookingHours,
      validateBufferTimeMinutes,
      validateNonNegativeNumber,
      validateDepositAmount,
    ]
  );

  const mergeStepErrorsIntoFormErrors = (prev, stepKeys, stepErrors) => {
    const next = { ...prev };
    stepKeys.forEach((k) => {
      delete next[k];
    });
    return { ...next, ...stepErrors };
  };

  /**
   * POST /customer/booking-templates & PUT /customer/booking-templates/template/:contactId
   * Body matches API schema (no category/categoryId).
   */
  const buildApiPayload = () => {
    const cap = Math.max(1, Number.parseInt(Number(formData.maxCapacity), 10) || 1);
    const reminderTimes = Array.isArray(formData.reminderTimes) && formData.reminderTimes.length
      ? formData.reminderTimes
        .map((t) => Math.max(0, Math.trunc(Number(t))))
        .filter((n) => !Number.isNaN(n))
      : [0];
    return {
      name: String(formData.name ?? "").trim(),
      description: String(formData.description ?? "").trim(),
      duration: Number(formData.duration) || 60,
      maxCapacity: cap,
      contactId: formData.contactId ? Number(formData.contactId) : 0,
      approvalType: formData.approvalType || "AUTO",
      isActive: formData.isActive,
      maxAdvanceBooking: clampIntInclusive(
        formData.maxAdvanceBooking,
        MAX_ADVANCE_BOOKING_DAYS_MIN,
        MAX_ADVANCE_BOOKING_DAYS_MAX,
        EMPTY_FORM.maxAdvanceBooking
      ),
      minAdvanceBooking: clampIntInclusive(
        formData.minAdvanceBooking,
        MIN_ADVANCE_BOOKING_HOURS_MIN,
        MIN_ADVANCE_BOOKING_HOURS_MAX,
        EMPTY_FORM.minAdvanceBooking
      ),
      bufferTime: clampIntInclusive(
        formData.bufferTime,
        BUFFER_TIME_MINUTES_MIN,
        BUFFER_TIME_MINUTES_MAX,
        EMPTY_FORM.bufferTime
      ),
      allowCancellation: formData.allowCancellation,
      cancellationDeadline: Number(formData.cancellationDeadline) || 168,
      requiresDeposit: formData.requiresDeposit,
      depositAmount: Number(formData.depositAmount) || 0,
      autoReminders: formData.autoReminders,
      reminderTimes,
      workingHours: formData.workingHours || EMPTY_FORM.workingHours,
      blackoutDates: Array.isArray(formData.blackoutDates)
        ? formData.blackoutDates.map(String)
        : [],
      ...buildPricingPayloadFields(formData),
    };
  };

  const handleSubmit = async () => {
    const { errors: validationErrors, firstTab } = validateTemplateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      if (currentTemplate) setActiveTab(firstTab);
      else setCreateStep(firstTab);
      return;
    }
    if (currentTemplate == null && formData.contactId && Number(formData.contactId) > 0) {
      // create with valid contact — continue submit
    } else if (currentTemplate == null) {
      setFormErrors({ contactId: t("pages.bookingTemplates.validation.required") });
      setCreateStep(0);
      return;
    }
    setFormErrors({});
    const payload = buildApiPayload();
    try {
      if (currentTemplate) {
        const contactId = currentTemplate.contactId ?? currentTemplate.id;
        if (!contactId) {
          toast.error(t("pages.bookingTemplates.toast.contactIdUpdate"));
          return;
        }
        await bookingTemplatesAPI.updateTemplate(contactId, payload);
        toast.success(t("pages.bookingTemplates.toast.updated"));
      } else {
        await bookingTemplatesAPI.createTemplate(payload);
        toast.success(t("pages.bookingTemplates.toast.created"));
      }
      setOpen(false);
      fetchTemplates();
    } catch (err) {
      console.error("Error saving template:", err);
      const msg = getApiErrorMessage(err, t("pages.bookingTemplates.toast.saveFailed"));
      if (isLikelyDurationFieldError(msg)) {
        setFormErrors((prev) => ({ ...prev, duration: msg }));
        if (currentTemplate) setActiveTab(0);
        else setCreateStep(0);
      }
      setError(msg);
      toast.error(msg);
    }
  };

  const handleOpenDeleteDialog = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    const contactId = templateToDelete.contactId ?? templateToDelete.id;
    if (!contactId) {
      toast.error(t("pages.bookingTemplates.toast.contactIdDelete"));
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      return;
    }
    try {
      await bookingTemplatesAPI.deleteTemplate(contactId);
      toast.success(t("pages.bookingTemplates.toast.deleted"));
      fetchTemplates();
    } catch (err) {
      const msg = getApiErrorMessage(err, t("pages.bookingTemplates.toast.deleteFailed"));
      setError(msg);
      toast.error(msg);
    } finally {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleToggleStatus = async (template) => {
    const contactId = template.contactId ?? template.id;
    if (!contactId) {
      toast.error(t("pages.bookingTemplates.toast.contactIdToggle"));
      return;
    }
    const nextIsActive = !template.isActive;
    try {
      await bookingTemplatesAPI.toggleTemplateStatus(contactId, nextIsActive);
      toast.success(nextIsActive ? t("pages.bookingTemplates.toast.toggleActivated") : t("pages.bookingTemplates.toast.toggleDeactivated"));
      fetchTemplates();
    } catch (err) {
      const msg = getApiErrorMessage(err, t("pages.bookingTemplates.toast.toggleFailed"));
      setError(msg); toast.error(msg);
    }
  };

  // Open in EDIT mode — GET /booking-templates/template/:contactId
  const handleEdit = async (template) => {
    const contactId = template.contactId ?? template.id;
    if (!contactId) {
      toast.error(t("pages.bookingTemplates.toast.contactIdEdit"));
      return;
    }
    setIsEditLoading(true);
    try {
      const response = await bookingTemplatesAPI.getTemplateByContactId(contactId);
      const raw = response?.data;
      const data = raw?.data ?? raw?.payload ?? raw ?? template;
      setFormErrors({});
      setCurrentTemplate(data);
      setActiveTab(0);
      setCreateStep(0);
      setFormData(mapApiResponseToForm(data, contactId));
      setOpen(true);
    } catch (err) {
      console.error("Error fetching template:", err);
      toast.error(getApiErrorMessage(err, t("pages.bookingTemplates.toast.loadEditFailed")));
      setFormData(mapApiResponseToForm(template, contactId));
      setCurrentTemplate(template);
      setCreateStep(0);
      setOpen(true);
    } finally {
      setIsEditLoading(false);
    }
  };

  // Open in CREATE mode
  const handleAdd = () => {
    setFormErrors({});
    setCurrentTemplate(null);
    setActiveTab(0);
    setCreateStep(0);
    setFormData(EMPTY_FORM);
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setCreateStep(0);
  };

  const isCreateWizard = !currentTemplate;

  const handleWizardNext = () => {
    if (createStep === 0) {
      let nameErr = "";
      if (!String(formData.name ?? "").trim()) {
        nameErr = t("pages.bookingTemplates.validation.required");
      }
      const capError = validateMaxCapacity(formData.maxCapacity);
      const durError = validateDuration(formData.duration);
      setFormErrors((prev) => ({
        ...prev,
        name: nameErr,
        maxCapacity: capError || "",
        duration: durError || "",
        contactId: "",
      }));
      if (nameErr || capError || durError) return;
      if (!formData.contactId || Number(formData.contactId) <= 0) {
        setFormErrors((prev) => ({
          ...prev,
          contactId: t("pages.bookingTemplates.validation.required"),
        }));
        return;
      }
      setFormErrors((prev) => (prev.contactId ? { ...prev, contactId: "" } : prev));
    } else if (createStep === 1) {
      const { errors: all } = validateTemplateForm(formData);
      const pick = {};
      AVAILABILITY_ERROR_KEYS.forEach((k) => {
        if (all[k]) pick[k] = all[k];
      });
      setFormErrors((prev) => mergeStepErrorsIntoFormErrors(prev, AVAILABILITY_ERROR_KEYS, pick));
      if (Object.keys(pick).length > 0) return;
    } else if (createStep === 2) {
      const { errors: all } = validateTemplateForm(formData);
      const pick = {};
      SETTINGS_ERROR_KEYS.forEach((k) => {
        if (all[k]) pick[k] = all[k];
      });
      setFormErrors((prev) => mergeStepErrorsIntoFormErrors(prev, SETTINGS_ERROR_KEYS, pick));
      if (Object.keys(pick).length > 0) return;
    }
    setCreateStep((s) => Math.min(3, s + 1));
  };

  const handleWizardPrevious = () => {
    setCreateStep((s) => Math.max(0, s - 1));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = clampBookingNumericInput(name, value);
    setFormData((prev) => ({ ...prev, [name]: next }));
    const clearFieldErrors = [
      "name",
      "duration",
      "maxAdvanceBooking",
      "minAdvanceBooking",
      "bufferTime",
      "cancellationDeadline",
      "depositAmount",
      "price",
    ];
    if (clearFieldErrors.includes(name)) {
      setFormErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
    if (name === "allowCancellation" && !checked) {
      setFormErrors((prev) => ({ ...prev, cancellationDeadline: "" }));
    }
    if (name === "requiresDeposit" && !checked) {
      setFormErrors((prev) => ({ ...prev, depositAmount: "" }));
    }
    if (name === "autoReminders" && !checked) {
      setFormErrors((prev) => ({ ...prev, reminderTimes: "" }));
    }
  };

  const handleWorkingHourChange = (day, field, value) => {
    const errKey = `workingHour_${day}`;
    setFormData((prev) => ({
      ...prev,
      workingHours: { ...prev.workingHours, [day]: { ...prev.workingHours[day], [field]: value } },
    }));
    setFormErrors((prev) => (prev[errKey] ? { ...prev, [errKey]: "" } : prev));
  };

  const handleAddReminderTime = (time) => {
    const parsed = Number.parseInt(String(time), 10);
    if (Number.isNaN(parsed) || parsed < 0) return;
    setFormData((prev) => {
      if (prev.reminderTimes.includes(parsed)) return prev;
      return { ...prev, reminderTimes: [...prev.reminderTimes, parsed] };
    });
    setFormErrors((prev) => (prev.reminderTimes ? { ...prev, reminderTimes: "" } : prev));
  };
  const handleRemoveReminderTime = (time) => {
    setFormData((prev) => {
      const idx = prev.reminderTimes.indexOf(time);
      if (idx < 0) return prev;
      return {
        ...prev,
        reminderTimes: [...prev.reminderTimes.slice(0, idx), ...prev.reminderTimes.slice(idx + 1)],
      };
    });
    setFormErrors((prev) => (prev.reminderTimes ? { ...prev, reminderTimes: "" } : prev));
  };

  const getContactById = (contactId) => {
    if (contactId == null || contactId === "") return null;
    return contacts.find((c) => c.id === contactId || c.id === Number(contactId)) || null;
  };

  /* ─── shared input disabled prop for view mode (functionality removed) ─── */
  const ro = false;
  const viewMode = false; // Shorthand for conditional rendering
  const stepIndex = isCreateWizard ? createStep : activeTab;

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, pb: 3, maxWidth: 1400, mx: 'auto', pr: { xs: 1, sm: 2 } }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center",
        justifyContent: "space-between", gap: 2, mb: 4, p: { xs: 2, sm: 4 },
        borderRadius: { xs: 2, sm: 4 },
        background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
        border: "1px solid rgba(255, 140, 66, 0.2)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}>
        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, letterSpacing: "-0.03em", color: GRAY_TEXT_DARK }}>
            {t("pages.bookingTemplates.pageTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: GRAY_TEXT }}>
            {t("pages.bookingTemplates.subtitle")}
          </Typography>
        </Box>

        {/* ── Styled Add Button ── */}
        <Button
          variant="contained"
          fullWidth={isMobile}
          startIcon={<AddIcon sx={{ fontSize: 22 }} />}
          onClick={handleAdd}
          sx={{
            px: 3.5,
            py: 1.4,
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.97rem",
            letterSpacing: "0.01em",
            background: ACCENT_COLOR,
            boxShadow: `0 4px 18px rgba(255, 140, 66, 0.4)`,
            border: "none",
            color: "#fff",
            transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
            "&:hover": {
              background: "#E06526",
              boxShadow: `0 8px 28px rgba(255, 140, 66, 0.5)`,
              transform: "translateY(-2px) scale(1.025)",
            },
            "&:active": {
              transform: "translateY(0) scale(0.99)",
            },
          }}
        >
          {t("pages.bookingTemplates.newTemplate")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Summary Cards (4 cards) — light pastel colors, large number, label, subtle background icon ── */}
      {!isLoading && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: "12px", sm: "20px" }, height: "95%", borderRadius: 4, position: "relative", overflow: "hidden",
              bgcolor: "#e8ecf8", border: "1px solid rgba(52, 73, 142, 0.1)",
            }}>
              <EventIcon sx={{ position: "absolute", right: -5, bottom: -5, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: "#34498e" }} />
              <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, mb: 1, color: "#34498e" }}>{templates.length}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#34498e", display: "block", lineHeight: 1.2 }}>{t("pages.bookingTemplates.stats.total")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: "12px", sm: "20px" }, height: "95%", borderRadius: 4, position: "relative", overflow: "hidden",
              bgcolor: "#e6fae6", border: "1px solid rgba(56, 150, 60, 0.1)",
            }}>
              <CheckIcon sx={{ position: "absolute", right: -5, bottom: -5, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: "#38963c" }} />
              <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, mb: 1, color: "#38963c" }}>
                {templates.filter((t) => !t.isActive).length}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#38963c", display: "block", lineHeight: 1.2 }}>
                {t("pages.bookingTemplates.card.inactive")}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: "12px", sm: "20px" }, height: "95%", borderRadius: 4, position: "relative", overflow: "hidden",
              bgcolor: "#ffe6e6", border: "1px solid rgba(220, 53, 69, 0.1)",
            }}>
              <CalendarIcon sx={{ position: "absolute", right: -5, bottom: -5, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: "#dc3545" }} />
              <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, mb: 1, color: "#dc3545" }}>
                {templates.reduce((sum, t) => sum + (t.bookingCount ?? t.bookingsCount ?? t.totalBookings ?? 0), 0)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#dc3545", display: "block", lineHeight: 1.2 }}>{t("pages.bookingTemplates.stats.totalBookings")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: "12px", sm: "20px" }, height: "95%", borderRadius: 4, position: "relative", overflow: "hidden",
              bgcolor: "#eaf5ff", border: "1px solid rgba(52, 73, 142, 0.1)",
            }}>
              <TimerIcon sx={{ position: "absolute", right: -5, bottom: -5, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: "#34498e" }} />
              <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, mb: 1, color: "#34498e" }}>
                {templates.length > 0
                  ? `${Math.round(templates.reduce((s, t) => s + (t.duration || 0), 0) / templates.length)}`
                  : "—"}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#34498e", display: "block", lineHeight: 1.2 }}>{t("pages.bookingTemplates.stats.avgDuration")}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Template Cards ── */}
      {(() => {
        if (isLoading) {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8 }}>
              <CircularProgress size={40} thickness={4} sx={{ color: ACCENT_COLOR }} />
              <Typography variant="body2" sx={{ mt: 2, color: GRAY_TEXT }}>{t("pages.bookingTemplates.loading")}</Typography>
            </Box>
          );
        }

        if (templates.length === 0) {
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2, border: "1px dashed", borderColor: GRAY_BORDER, bgcolor: GRAY_LIGHT }}>
                  <BookIcon sx={{ fontSize: 64, color: GRAY_TEXT, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: GRAY_TEXT_DARK }} gutterBottom>{t("pages.bookingTemplates.empty.title")}</Typography>
                  <Typography variant="body2" sx={{ mb: 2, maxWidth: 400, mx: "auto", color: GRAY_TEXT }}>
                    {t("pages.bookingTemplates.empty.hint")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          );
        }

        return (
          <Grid container spacing={3}>
            {[...templates]
              .sort((a, b) => (b?.isActive ? 1 : 0) - (a?.isActive ? 1 : 0))
              .map((template) => {
                const rowKey = template.contactId ?? template.id ?? template.name;
                return (
                  <Grid item xs={12} md={6} lg={4} key={rowKey}>
                    <Paper elevation={0} sx={{
                      p: 3, height: "100%", display: "flex", flexDirection: "column",
                      borderRadius: 4, border: "1px solid", borderColor: GRAY_BORDER,
                      position: "relative", overflow: "hidden", background: "#ffffff",
                      transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        borderColor: ACCENT_COLOR,
                        boxShadow: `0 15px 35px rgba(79,195,247,0.15)`,
                        "& .card-actions": { opacity: 1 },
                        "& .bg-accent": { transform: "scale(1.2) translate(-10%,-10%)" },
                      },
                    }}>
                      {/* BG Accent */}
                      <Box className="bg-accent" sx={{
                        position: "absolute", top: 0, right: 0, width: 120, height: 120,
                        background: `radial-gradient(circle, rgba(79,195,247,0.08) 0%, rgba(79,195,247,0) 70%)`,
                        borderRadius: "0 0 0 100%", transition: "transform 0.5s ease", zIndex: 0,
                      }} />

                      {/* Card Header: Icon + Title + Status */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, zIndex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                          <BookIcon sx={{ fontSize: 28, color: ACCENT_COLOR, flexShrink: 0 }} />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }} noWrap>
                            {template.name}
                          </Typography>
                        </Box>
                        {template.isActive ? (
                          <Chip
                            icon={<CheckIcon sx={{ fontSize: 14, color: `${ACCENT_COLOR} !important` }} />}
                            label={t("pages.bookingTemplates.card.active")}
                            sx={{
                              bgcolor: "rgba(79,195,247,0.12)", color: GRAY_TEXT_DARK, fontWeight: 700, borderRadius: "8px", border: "1px solid", borderColor: GRAY_BORDER, "& .MuiChip-icon": { color: ACCENT_COLOR },
                            }}
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<WarningIcon sx={{ fontSize: 14, color: GRAY_TEXT }} />}
                            label={t("pages.bookingTemplates.card.inactive")}
                            variant="outlined"
                            size="small"
                            sx={{
                              borderRadius: "8px", color: GRAY_TEXT, borderColor: GRAY_BORDER,
                            }}
                          />
                        )}
                      </Box>

                      {/* Price tag */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, zIndex: 1 }}>
                        <Chip label={getTemplatePriceChipLabel(template, t)}
                          size="small" sx={{ height: 22, fontSize: "0.7rem", bgcolor: "rgba(79,195,247,0.12)", color: GRAY_TEXT_DARK, fontWeight: 600, borderRadius: "6px" }} />
                      </Box>

                      {template.description && (
                        <Typography variant="body2" sx={{
                          mb: 2, lineHeight: 1.6, zIndex: 1, color: GRAY_TEXT,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {template.description}
                        </Typography>
                      )}

                      {/* 3 Metrics with Icons (Duration, Max Participants, Bookings) */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, zIndex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TimerIcon sx={{ fontSize: 18, color: GRAY_TEXT }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: GRAY_TEXT_DARK }}>{t("pages.bookingTemplates.card.minutes", { n: template.duration ?? 0 })}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 18, color: GRAY_TEXT }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: GRAY_TEXT_DARK }}>{getMaxParticipantsLabel(template, t)}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 18, color: GRAY_TEXT }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: GRAY_TEXT_DARK }}>
                            {t("pages.bookingTemplates.card.bookings", { n: template.bookingCount ?? template.bookingsCount ?? template.totalBookings ?? 0 })}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Assigned Users — names as chips */}
                      <Box sx={{ mb: 2, zIndex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.75, color: GRAY_TEXT_DARK }}>{t("pages.bookingTemplates.card.assignedUsers")}</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {(() => {
                            const contact = getContactById(template.contactId);
                            if (!contact) {
                              return (
                                <Chip
                                  label={template.contactTitle || t("pages.bookingTemplates.card.noContact")}
                                  size="small"
                                  sx={{
                                    height: 26, fontSize: "0.8rem", fontWeight: 500,
                                    bgcolor: GRAY_LIGHT, color: GRAY_TEXT, borderRadius: "8px",
                                    border: "1px solid", borderColor: GRAY_BORDER,
                                  }}
                                />
                              );
                            }
                            const displayName = contact.contactPerson || contact.title || contact.name || "—";
                            return (
                              <Chip
                                key={contact.id}
                                label={displayName}
                                size="small"
                                sx={{
                                  height: 26, fontSize: "0.8rem", fontWeight: 500,
                                  bgcolor: "#fff", color: GRAY_TEXT_DARK, borderRadius: "8px",
                                  border: "1px solid", borderColor: GRAY_BORDER,
                                  "&:hover": { bgcolor: GRAY_LIGHT },
                                }}
                              />
                            );
                          })()}
                        </Box>
                      </Box>

                      {/* Footer: Approval status | View Edit Delete — all horizontal */}
                      <Box sx={{ mt: "auto", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, zIndex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: GRAY_TEXT }}>
                          {getApprovalTypeLabel(template.approvalType, t)}
                        </Typography>
                        <Box className="card-actions" sx={{
                          display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center",
                          opacity: 1,
                          transition: "opacity 0.2s ease",
                        }}>
                          <Tooltip title={template.isActive ? t("pages.bookingTemplates.deactivate") : t("pages.bookingTemplates.activate")}>
                            <IconButton size="small" onClick={() => handleToggleStatus(template)}
                              sx={{
                                width: 34, height: 34,
                                bgcolor: template.isActive ? "rgba(56, 150, 60, 0.08)" : "rgba(220, 53, 69, 0.08)",
                                border: "1px solid",
                                borderColor: template.isActive ? "rgba(56, 150, 60, 0.3)" : "rgba(220, 53, 69, 0.3)",
                                color: template.isActive ? "#38963c" : "#dc3545",
                                "&:hover": {
                                  bgcolor: template.isActive ? "#38963c" : "#dc3545",
                                  color: "#fff",
                                  borderColor: template.isActive ? "#38963c" : "#dc3545",
                                  transform: "scale(1.1)",
                                },
                              }}>
                              {template.isActive ? <ToggleOnIcon sx={{ fontSize: 20 }} /> : <ToggleOffIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.edit")}>
                            <IconButton size="small" onClick={() => handleEdit(template)} disabled={isEditLoading}
                              sx={{
                                width: 34, height: 34,
                                bgcolor: "rgba(79,195,247,0.08)", border: "1px solid rgba(79,195,247,0.3)",
                                color: ACCENT_COLOR,
                                "&:hover": { bgcolor: ACCENT_COLOR, color: "#fff", borderColor: ACCENT_COLOR, transform: "scale(1.1)" },
                              }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.delete")}>
                            <IconButton size="small" onClick={() => handleOpenDeleteDialog(template)}
                              sx={{
                                width: 34, height: 34,
                                bgcolor: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.3)",
                                color: "#e53935",
                                "&:hover": { bgcolor: "#e53935", color: "#fff", borderColor: "#e53935", transform: "scale(1.1)" },
                              }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>
        );
      })()}

      {/* ════════════ Dialog (layout aligned with Contacts dialog) ════════════ */}
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, maxHeight: isMobile ? "100%" : "90vh" } }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#ffffff",
            px: 3,
            py: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#000000" }}>
            {t("pages.bookingTemplates.dialog.title", {
              action: currentTemplate ? t("pages.bookingTemplates.dialog.edit") : t("pages.bookingTemplates.dialog.create"),
            })}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={handleDialogClose} aria-label="close" size="small" sx={{ color: "rgba(0,0,0,0.54)" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, bgcolor: "#f8f9fa" }}>
          {isCreateWizard ? (
            <Stepper
              activeStep={createStep}
              alternativeLabel
              sx={{
                py: 2,
                "& .MuiStepLabel-label": { fontSize: "0.78rem", fontWeight: 600 },
              }}
            >
              <Step>
                <StepLabel>{t("pages.bookingTemplates.tab.general")}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t("pages.bookingTemplates.tab.availability")}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t("pages.bookingTemplates.tab.settings")}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t("pages.bookingTemplates.tab.pricing")}</StepLabel>
              </Step>
            </Stepper>
          ) : (
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 64, fontSize: "0.95rem" } }}
            >
              <Tab icon={<InfoIcon />} iconPosition="start" label={t("pages.bookingTemplates.tab.general")} />
              <Tab icon={<ScheduleIcon />} iconPosition="start" label={t("pages.bookingTemplates.tab.availability")} />
              <Tab icon={<SettingsIcon />} iconPosition="start" label={t("pages.bookingTemplates.tab.settings")} />
              <Tab icon={<MoneyIcon />} iconPosition="start" label={t("pages.bookingTemplates.tab.pricing")} />
            </Tabs>
          )}
        </Box>

        <DialogContent sx={{ px: { xs: 2, sm: 4 }, pb: 4, pt: { xs: 3, sm: 5 } }}>
          <Grid container spacing={3}>
            {/* ── General Tab ── */}
            {stepIndex === 0 && (
              <>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t("pages.bookingTemplates.field.templateName")}
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required={!ro}
                    disabled={ro}
                    error={Boolean(formErrors.name)}
                    helperText={formErrors.name || ""}
                    InputProps={{ startAdornment: <InfoIcon sx={{ color: "action.active", mr: 1, fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField name="description" label={t("pages.bookingTemplates.field.description")} value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value.slice(0, 500) }))}
                    fullWidth multiline rows={3} disabled={ro} inputProps={{ maxLength: 500 }} helperText={`${(formData.description || "").length}/500`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="duration"
                    label={t("pages.bookingTemplates.field.duration")}
                    type="number"
                    value={formData.duration}
                    onChange={handleChange}
                    fullWidth
                    required={!ro}
                    disabled={ro}
                    error={Boolean(formErrors.duration)}
                    helperText={formErrors.duration || ""}
                    inputProps={{ min: 15, max: 1440, step: 1 }}
                    InputProps={{ startAdornment: <TimerIcon sx={{ color: "action.active", mr: 1, fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="maxCapacity"
                    label={t("pages.bookingTemplates.field.maxParticipants")}
                    type="number"
                    value={formData.maxCapacity}
                    disabled={ro}
                    required={!ro}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const parsed = parseMaxCapacityInput(raw);
                      setFormData((prev) => ({ ...prev, maxCapacity: parsed }));
                      if (formErrors.maxCapacity)
                        setFormErrors((prev) => ({ ...prev, maxCapacity: validateMaxCapacity(parsed) }));
                    }}
                    fullWidth
                    error={Boolean(formErrors.maxCapacity)}
                    helperText={formErrors.maxCapacity}
                    inputProps={{ min: 1, step: 1 }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <PeopleIcon sx={{ color: "action.active", mr: 1, fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={ro}>
                    <InputLabel>{t("pages.bookingTemplates.field.approvalType")}</InputLabel>
                    <Select name="approvalType" value={formData.approvalType} onChange={handleChange} label={t("pages.bookingTemplates.field.approvalType")}
                      startAdornment={<RuleIcon sx={{ color: "action.active", mr: 1, fontSize: 20 }} />}>
                      <MenuItem value="AUTO">{t("pages.bookingTemplates.menu.approvalAuto")}</MenuItem>
                      <MenuItem value="MANUAL">{t("pages.bookingTemplates.menu.approvalManual")}</MenuItem>
                      <MenuItem value="PARTIAL">{t("pages.bookingTemplates.menu.approvalPartial")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, color: GRAY_TEXT_DARK, mb: 0.5 }}>
                    {t("pages.bookingTemplates.assignUsersTitle")}
                    {!ro ? (
                      <Typography component="span" sx={{ color: "error.main", ml: 0.25 }} aria-hidden>
                        *
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="body2" sx={{ color: GRAY_TEXT, mb: 1 }}>{t("pages.bookingTemplates.assignUsersHint")}</Typography>
                  {Boolean(formErrors.contactId) && !ro ? (
                    <FormHelperText error sx={{ mx: 0, mb: 1 }}>{formErrors.contactId}</FormHelperText>
                  ) : null}
                  <Box sx={{ maxHeight: 320, overflow: "auto", border: "1px solid", borderColor: formErrors.contactId ? "error.main" : GRAY_BORDER, borderRadius: 2, bgcolor: "#fff", p: 1 }}>
                    {contacts.length === 0 ? (
                      <Typography variant="body2" sx={{ color: GRAY_TEXT, py: 2, textAlign: "center" }}>{t("pages.bookingTemplates.noContacts")}</Typography>
                    ) : (
                      contacts.map((c) => {
                        const isSelected = String(formData.contactId ?? "") === String(c.id);
                        return (
                          <Paper
                            key={c.id}
                            elevation={0}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: isSelected ? ACCENT_COLOR : GRAY_BORDER,
                              bgcolor: isSelected ? "rgba(79,195,247,0.06)" : "#fff",
                              cursor: ro ? "default" : "pointer",
                              transition: "all 0.2s",
                              "&:last-of-type": { mb: 0 },
                              "&:hover": ro ? {} : { borderColor: ACCENT_COLOR, bgcolor: "rgba(79,195,247,0.04)" },
                            }}
                            onClick={() => {
                              if (ro) return;
                              setFormData((prev) => ({ ...prev, contactId: isSelected ? "" : c.id }));
                              setFormErrors((prev) => (prev.contactId ? { ...prev, contactId: "" } : prev));
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => {
                                  if (ro) return;
                                  setFormData((prev) => ({ ...prev, contactId: isSelected ? "" : c.id }));
                                  setFormErrors((prev) => (prev.contactId ? { ...prev, contactId: "" } : prev));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={ro}
                                sx={{ color: GRAY_BORDER, "&.Mui-checked": { color: ACCENT_COLOR }, mt: -0.5 }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>
                                  {c.title || t("bookingForm.contactLabel", { id: c.id })}
                                </Typography>
                                {(c.contactPerson || c.name) && (
                                  <Typography variant="body2" sx={{ color: GRAY_TEXT, display: "block" }}>
                                    {c.contactPerson || c.name}
                                  </Typography>
                                )}
                              </Box>
                              {(c.subCategoryName || c.categoryName) && (
                                <Chip
                                  label={c.subCategoryName || c.categoryName}
                                  size="small"
                                  sx={{ height: 22, fontSize: "0.7rem", bgcolor: GRAY_LIGHT, color: GRAY_TEXT_DARK, borderRadius: "8px", flexShrink: 0 }}
                                />
                              )}
                            </Box>
                          </Paper>
                        );
                      })
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="isActive" checked={formData.isActive} onChange={handleSwitchChange} disabled={ro} />}
                    label={t("pages.bookingTemplates.field.active")} />
                </Grid>
              </>
            )}

            {/* ── Availability Tab ── */}
            {stepIndex === 1 && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="maxAdvanceBooking"
                    label={t("pages.bookingTemplates.field.maxAdvanceDays")}
                    type="number"
                    value={formData.maxAdvanceBooking}
                    onChange={handleChange}
                    fullWidth
                    required={!ro}
                    disabled={ro}
                    error={Boolean(formErrors.maxAdvanceBooking)}
                    helperText={formErrors.maxAdvanceBooking || ""}
                    inputProps={{ min: MAX_ADVANCE_BOOKING_DAYS_MIN, max: MAX_ADVANCE_BOOKING_DAYS_MAX, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="minAdvanceBooking"
                    label={t("pages.bookingTemplates.field.minAdvanceHours")}
                    type="number"
                    value={formData.minAdvanceBooking}
                    onChange={handleChange}
                    fullWidth
                    required={!ro}
                    disabled={ro}
                    error={Boolean(formErrors.minAdvanceBooking)}
                    helperText={formErrors.minAdvanceBooking || ""}
                    inputProps={{ min: MIN_ADVANCE_BOOKING_HOURS_MIN, max: MIN_ADVANCE_BOOKING_HOURS_MAX, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="bufferTime"
                    label={t("pages.bookingTemplates.field.buffer")}
                    type="number"
                    value={formData.bufferTime}
                    onChange={handleChange}
                    fullWidth
                    required={!ro}
                    disabled={ro}
                    error={Boolean(formErrors.bufferTime)}
                    helperText={formErrors.bufferTime || ""}
                    inputProps={{ min: BUFFER_TIME_MINUTES_MIN, max: BUFFER_TIME_MINUTES_MAX, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t("pages.bookingTemplates.workingHours")}</Typography>
                  {Object.keys(formData.workingHours).map((day) => {
                    const whErr = formErrors[`workingHour_${day}`] || "";
                    return (
                      <Box key={day} sx={{ mb: 2, p: 2, border: "1px solid", borderColor: whErr ? "error.main" : GRAY_BORDER, borderRadius: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ fontWeight: 700 }}>{t(`pages.bookingTemplates.day.${day}`)}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              type="time"
                              label={t("pages.bookingTemplates.field.workingHoursStart")}
                              value={formData.workingHours[day].start}
                              fullWidth
                              required={Boolean(formData.workingHours[day].enabled) && !ro}
                              InputLabelProps={{ shrink: true }}
                              error={Boolean(whErr)}
                              onChange={(e) => handleWorkingHourChange(day, "start", e.target.value)}
                              disabled={ro || !formData.workingHours[day].enabled}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              type="time"
                              label={t("pages.bookingTemplates.field.workingHoursEnd")}
                              value={formData.workingHours[day].end}
                              fullWidth
                              required={Boolean(formData.workingHours[day].enabled) && !ro}
                              InputLabelProps={{ shrink: true }}
                              error={Boolean(whErr)}
                              onChange={(e) => handleWorkingHourChange(day, "end", e.target.value)}
                              disabled={ro || !formData.workingHours[day].enabled}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={formData.workingHours[day].enabled}
                                  disabled={ro}
                                  onChange={(e) => handleWorkingHourChange(day, "enabled", e.target.checked)}
                                />
                              )}
                              label={t("pages.bookingTemplates.field.enabled")}
                            />
                          </Grid>
                          {whErr ? (
                            <Grid item xs={12}>
                              <FormHelperText error sx={{ mx: 0 }}>{whErr}</FormHelperText>
                            </Grid>
                          ) : null}
                        </Grid>
                      </Box>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* ── Settings Tab ── */}
            {stepIndex === 2 && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="allowCancellation" checked={formData.allowCancellation} onChange={handleSwitchChange} disabled={ro} />}
                    label={t("pages.bookingTemplates.settings.allowCancel")} />
                </Grid>
                {formData.allowCancellation && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="cancellationDeadline"
                      label={t("pages.bookingTemplates.settings.cancelDeadline")}
                      type="number"
                      value={formData.cancellationDeadline}
                      onChange={handleChange}
                      fullWidth
                      required={formData.allowCancellation && !ro}
                      disabled={ro}
                      error={Boolean(formErrors.cancellationDeadline)}
                      helperText={formErrors.cancellationDeadline || ""}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="requiresDeposit" checked={formData.requiresDeposit} onChange={handleSwitchChange} disabled={ro} />}
                    label={t("pages.bookingTemplates.settings.requiresDeposit")} />
                </Grid>
                {formData.requiresDeposit && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="depositAmount"
                      label={t("pages.bookingTemplates.settings.depositAmount")}
                      type="number"
                      value={formData.depositAmount}
                      onChange={handleChange}
                      fullWidth
                      required={formData.requiresDeposit && !ro}
                      disabled={ro}
                      error={Boolean(formErrors.depositAmount)}
                      helperText={formErrors.depositAmount || ""}
                      inputProps={{ min: 0, step: "any" }}
                      InputProps={{ startAdornment: <span>$</span> }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="autoReminders" checked={formData.autoReminders} onChange={handleSwitchChange} disabled={ro} />}
                    label={t("pages.bookingTemplates.settings.autoReminders")} />
                </Grid>
                {formData.autoReminders && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" component="div">
                      {t("pages.bookingTemplates.settings.reminderTitle")}
                      {!ro ? (
                        <Typography component="span" sx={{ color: "error.main", ml: 0.25 }} aria-hidden>
                          *
                        </Typography>
                      ) : null}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
                      {formData.reminderTimes.map((time) => (
                        <Chip
                          key={`reminder-${time}`}
                          label={t("pages.bookingTemplates.settings.hoursChip", { n: time })}
                          onDelete={ro ? undefined : () => handleRemoveReminderTime(time)}
                        />
                      ))}
                    </Box>
                    {Boolean(formErrors.reminderTimes) && (
                      <FormHelperText error sx={{ mt: 0, mb: 1 }}>{formErrors.reminderTimes}</FormHelperText>
                    )}
                    {!ro && (
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <TextField id="new-reminder-time" label={t("pages.bookingTemplates.settings.hoursLabel")} type="number" size="small"
                          inputProps={{ min: 0, step: 1 }} />
                        <Button variant="outlined" onClick={() => {
                          const input = document.getElementById("new-reminder-time");
                          if (!input || input.value === "") return;
                          handleAddReminderTime(input.value);
                          input.value = "";
                        }}>{t("pages.bookingTemplates.settings.add")}</Button>
                      </Box>
                    )}
                  </Grid>
                )}
              </>
            )}

            {/* ── Pricing Tab ── */}
            {stepIndex === 3 && (
              <>
                <Grid item xs={12}>
                  <RadioGroup
                    name="pricingType"
                    value={getFormPricingType(formData)}
                    onChange={(e) => {
                      if (ro) return;
                      const v = e.target.value;
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        PRICING_ERROR_KEYS.forEach((k) => {
                          delete next[k];
                        });
                        return next;
                      });
                      setFormData((prev) => applyPricingTypeChange(prev, v));
                    }}
                  >
                    <FormControlLabel value="none" control={<Radio disabled={ro} />} label={t("pages.bookingTemplates.pricing.none")} />
                    <FormControlLabel value="fixed" control={<Radio disabled={ro} />} label={t("pages.bookingTemplates.pricing.fixed")} />
                    <FormControlLabel value="range" control={<Radio disabled={ro} />} label={t("pages.bookingTemplates.pricing.range")} />
                  </RadioGroup>
                </Grid>
                {formData.price != null && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="price"
                      label={t("pages.bookingTemplates.field.price")}
                      type="number"
                      value={formData.price}
                      onChange={handleChange}
                      fullWidth
                      required={!ro}
                      disabled={ro}
                      error={Boolean(formErrors.price)}
                      helperText={formErrors.price || ""}
                      inputProps={{ min: 0, step: "any" }}
                      InputProps={{ startAdornment: <span>$</span> }}
                    />
                  </Grid>
                )}
                {formData.priceRange != null && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t("pages.bookingTemplates.field.minPrice")}
                        type="number"
                        value={formData.priceRange.min}
                        fullWidth
                        required={!ro}
                        disabled={ro}
                        error={Boolean(formErrors.priceRangeMin)}
                        helperText={formErrors.priceRangeMin || ""}
                        inputProps={{ min: 0, step: "any" }}
                        InputProps={{ startAdornment: <span>$</span> }}
                        onChange={(e) => {
                          const v = e.target.value;
                          const min = v === "" ? "" : Math.max(0, Number(v));
                          setFormData((prev) => ({
                            ...prev,
                            priceRange: { ...prev.priceRange, min },
                          }));
                          setFormErrors((prev) => ({ ...prev, priceRangeMin: "", priceRangeMax: "" }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t("pages.bookingTemplates.field.maxPrice")}
                        type="number"
                        value={formData.priceRange.max}
                        fullWidth
                        required={!ro}
                        disabled={ro}
                        error={Boolean(formErrors.priceRangeMax)}
                        helperText={formErrors.priceRangeMax || ""}
                        inputProps={{ min: 0, step: "any" }}
                        InputProps={{ startAdornment: <span>$</span> }}
                        onChange={(e) => {
                          const v = e.target.value;
                          const max = v === "" ? "" : Math.max(0, Number(v));
                          setFormData((prev) => ({
                            ...prev,
                            priceRange: { ...prev.priceRange, max },
                          }));
                          setFormErrors((prev) => ({ ...prev, priceRangeMin: "", priceRangeMax: "" }));
                        }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>

        {/* ── Dialog Actions ── */}
        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "space-between",
            bgcolor: "#f8f9fa",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: "10px",
              color: GRAY_TEXT,
              borderColor: GRAY_BORDER,
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)", borderColor: GRAY_TEXT },
            }}
          >
            {t("common.cancel")}
          </Button>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", flex: "1 1 auto" }}>
            {isCreateWizard && createStep > 0 && (
              <Button
                onClick={handleWizardPrevious}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: "10px",
                  borderColor: GRAY_BORDER,
                  color: GRAY_TEXT_DARK,
                }}
              >
                {t("pages.bookingTemplates.wizard.previous")}
              </Button>
            )}
            {isCreateWizard && createStep < 3 && (
              <Button
                onClick={handleWizardNext}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  borderRadius: "10px",
                  background: ACCENT_COLOR,
                  boxShadow: `0 4px 14px rgba(255, 140, 66, 0.4)`,
                  "&:hover": {
                    background: "#E06526",
                    boxShadow: `0 6px 20px rgba(255, 140, 66, 0.5)`,
                  },
                }}
              >
                {t("pages.bookingTemplates.wizard.next")}
              </Button>
            )}
            {isCreateWizard && createStep === 3 && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  borderRadius: "10px",
                  background: ACCENT_COLOR,
                  boxShadow: `0 4px 14px rgba(255, 140, 66, 0.4)`,
                  "&:hover": {
                    background: "#E06526",
                    boxShadow: `0 6px 20px rgba(255, 140, 66, 0.5)`,
                  },
                }}
              >
                {t("pages.bookingTemplates.wizard.createBooking")}
              </Button>
            )}
            {!isCreateWizard && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  borderRadius: "10px",
                  background: ACCENT_COLOR,
                  boxShadow: `0 4px 14px rgba(255, 140, 66, 0.4)`,
                  "&:hover": {
                    background: "#E06526",
                    boxShadow: `0 6px 20px rgba(255, 140, 66, 0.5)`,
                  },
                }}
              >
                {currentTemplate ? t("pages.bookingTemplates.actions.saveChanges") : t("pages.bookingTemplates.actions.create")}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#1E293B', pt: 3.5, px: 3.5, pb: 1.5 }}>
          {t("pages.bookingTemplates.confirm.deleteTitle")}
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, py: 0 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.96rem', lineHeight: 1.55 }}>
            {t("pages.bookingTemplates.confirm.delete")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3.5, pb: 3.5, pt: 3, gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#64748B',
              px: 2,
              py: 0.8,
              borderRadius: '10px',
              '&:hover': { bgcolor: '#F1F5F9', color: '#334155' }
            }}
          >
            {t("pages.bookingTemplates.btn.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteTemplate}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              bgcolor: '#D92D20',
              color: '#FFFFFF',
              px: 2.8,
              py: 0.9,
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(217, 45, 32, 0.25)',
              '&:hover': { bgcolor: '#B42318', boxShadow: '0 4px 12px rgba(217, 45, 32, 0.35)' }
            }}
          >
            {t("pages.bookingTemplates.btn.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
